import type { TFunction } from 'i18next';
import { ResolveTaskRequest, TaskDetailResolve } from '@/api';

/**
 * The words the app puts on an alert's resolution.
 *
 * Two screens show the same three enums: `ResolveAlertModal` offers them as
 * options, and `TaskEventsList` reads them back off a resolved event. They used
 * to spell them separately — the modal from hardcoded labels, the event list by
 * capitalising the API constant — and had already drifted apart. Everything
 * that names one of these values goes through here, so there is one set of
 * words and one place to change them.
 *
 * The lists below are also the modal's option order, so adding a value to the
 * API means adding it once, here.
 */

export type ResolveReason = NonNullable<ResolveTaskRequest['reasons']>[number];
export type Injury = NonNullable<TaskDetailResolve['injury']>;
export type EmergencyService = NonNullable<
    ResolveTaskRequest['emergencyServices']
>[number];

/** "Other" leads; the rest read alphabetically. */
export const RESOLVE_REASONS: ResolveReason[] = [
    'OTHER',
    'CONTACT_WITH_ELECTRICITY',
    'CONTACT_WITH_MACHINERY',
    'DROWNED_OR_ASPHYXIATED',
    'EXPOSED_TO_EXPLOSION',
    'EXPOSED_TO_FIRE',
    'EXPOSED_TO_HARMFUL_SUBSTANCE',
    'FALL_FROM_HEIGHT',
    'INJURED_BY_ANIMAL',
    'LIFTING_AND_HANDLING',
    'PHYSICAL_ASSAULT_OR_ACT_OF_VIOLENCE',
    'SLIP_TRIP_FALL_SAME_HEIGHT',
    'STRUCK_AGAINST',
    'STRUCK_BY_MOVING_OBJECT',
    'STRUCK_BY_OBJECT',
    'TRAPPED_BY_SOMETHING_COLLAPSING'
];

/**
 * `UNKNOWN` is deliberately absent: the API can hand it back on an event, so
 * `getInjuryLabel` names it, but nobody should be able to choose it.
 */
export const INJURY_OPTIONS: Injury[] = ['NONE', 'MINOR', 'MAJOR'];

export const EMERGENCY_SERVICES: EmergencyService[] = [
    'AMBULANCE',
    'FIRE',
    'POLICE'
];

/**
 * Takes a `string` rather than `ResolveReason` because the same values arrive
 * off an event, where the generated types are only as good as the spec.
 */
export function getResolveReasonLabel(t: TFunction, reason: string): string {
    switch (reason) {
        case 'OTHER':
            return t('shared.tasks.resolve_reasons.other');
        case 'CONTACT_WITH_ELECTRICITY':
            return t('shared.tasks.resolve_reasons.contact_with_electricity');
        case 'CONTACT_WITH_MACHINERY':
            return t('shared.tasks.resolve_reasons.contact_with_machinery');
        case 'DROWNED_OR_ASPHYXIATED':
            return t('shared.tasks.resolve_reasons.drowned_or_asphyxiated');
        case 'EXPOSED_TO_EXPLOSION':
            return t('shared.tasks.resolve_reasons.exposed_to_explosion');
        case 'EXPOSED_TO_FIRE':
            return t('shared.tasks.resolve_reasons.exposed_to_fire');
        case 'EXPOSED_TO_HARMFUL_SUBSTANCE':
            return t(
                'shared.tasks.resolve_reasons.exposed_to_harmful_substance'
            );
        case 'FALL_FROM_HEIGHT':
            return t('shared.tasks.resolve_reasons.fall_from_height');
        case 'INJURED_BY_ANIMAL':
            return t('shared.tasks.resolve_reasons.injured_by_animal');
        case 'LIFTING_AND_HANDLING':
            return t('shared.tasks.resolve_reasons.lifting_and_handling');
        case 'PHYSICAL_ASSAULT_OR_ACT_OF_VIOLENCE':
            return t(
                'shared.tasks.resolve_reasons.physical_assault_or_act_of_violence'
            );
        case 'SLIP_TRIP_FALL_SAME_HEIGHT':
            return t('shared.tasks.resolve_reasons.slip_trip_fall_same_height');
        case 'STRUCK_AGAINST':
            return t('shared.tasks.resolve_reasons.struck_against');
        case 'STRUCK_BY_MOVING_OBJECT':
            return t('shared.tasks.resolve_reasons.struck_by_moving_object');
        case 'STRUCK_BY_OBJECT':
            return t('shared.tasks.resolve_reasons.struck_by_object');
        case 'TRAPPED_BY_SOMETHING_COLLAPSING':
            return t(
                'shared.tasks.resolve_reasons.trapped_by_something_collapsing'
            );
        default:
            return t('shared.labels.unknown');
    }
}

export function getInjuryLabel(t: TFunction, injury: string): string {
    switch (injury) {
        case 'NONE':
            return t('shared.tasks.injuries.none');
        case 'MINOR':
            return t('shared.tasks.injuries.minor');
        case 'MAJOR':
            return t('shared.tasks.injuries.major');
        case 'UNKNOWN':
            return t('shared.tasks.injuries.unknown');
        default:
            return t('shared.labels.unknown');
    }
}

/**
 * `openapi.json` declares `TaskDetailResolve.emergencyServices` with the
 * *reasons* enum, so the generated type lies about what an event carries. The
 * runtime values are the three below — what `ResolveTaskRequest` sends — hence
 * the `string` parameter.
 */
export function getEmergencyServiceLabel(
    t: TFunction,
    service: string
): string {
    switch (service) {
        case 'AMBULANCE':
            return t('shared.tasks.emergency_services.ambulance');
        case 'FIRE':
            return t('shared.tasks.emergency_services.fire');
        case 'POLICE':
            return t('shared.tasks.emergency_services.police');
        default:
            return t('shared.labels.unknown');
    }
}
